import { StateStorage } from 'zustand/middleware'
import { createStore } from '../.'
import { Permission, Scope } from '../../interfaces/permissions'

interface HostPermissions {
  [host: string]: Permission
}

interface PrincipalPermissions {
  [principal: string]: HostPermissions
}

export interface PermissionStore {
  permissions: PrincipalPermissions
  getPermission: (principal: string, host: string) => Permission | undefined
  addPermission: ({
    host,
    principal,
    scopes,
    url,
    logo,
    name,
  }: {
    host: string
    principal: string
    scopes: Scope[]
    url: string
    logo?: string
    name?: string
  }) => void
  removePermission: (principal: string, host: string) => void
  addPermissionScope: (host: string, principal: string, method: Scope) => void
  clear: () => void
}

export const createPermissionsStore = (storage: StateStorage) =>
  createStore<PermissionStore>(
    storage,
    (set, get, store) => ({
      permissions: {},
      getPermission: (principal, host) => {
        return get().permissions[principal]?.[host]
      },
      addPermission: ({ host, principal, scopes, url, logo, name }) => {
        const { permissions } = get()
        const principalPermissions = permissions[principal] || {}

        set({
          permissions: {
            ...permissions,
            [principal]: {
              ...principalPermissions,
              [host]: {
                host,
                scopes,
                url,
                logo,
                name,
              },
            },
          },
        })
      },
      removePermission: (principal, host) => {
        const { permissions } = get()
        const { [host]: _, ...principalPermissions } = permissions[principal] || {}

        set({
          permissions: {
            ...permissions,
            [principal]: {
              ...principalPermissions,
            },
          },
        })
      },
      clear: () => {
        set(store.getInitialState())
      },
      addPermissionScope: (principal, host, newPermission) => {
        const { permissions } = get()
        const principalPermissions = permissions[principal] || {}
        const permission = principalPermissions[host] || {}

        const existingPermission = permission.scopes.find((p) => {
          return p.method === newPermission.method
        })

        if (existingPermission) {
          return
        }

        set({
          permissions: {
            ...permissions,
            [principal]: {
              ...principalPermissions,
              [host]: {
                ...permission,
                scopes: [...permission.scopes, newPermission],
              },
            },
          },
        })
      },
    }),
    {
      persist: {
        name: 'plugPermissions',
        version: 0,
      },
    }
  )
